import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faTimes, faBolt } from '@fortawesome/free-solid-svg-icons';
import DealChatbotEnhanced from './DealChatbotEnhanced';
import llmAxiosInstance from 'helpers/llmApi';
import axiosInstance from 'helpers/api';
import { triggerDealCachePreload } from 'services/dealChatbot';
import './DealChatbot.css';

interface DealChatbotIntegrationProps {
  dealId: string;
  userId: string;
  dealStage?: string;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  message: string;
  timestamp: Date;
  cached?: boolean;
  latency?: number;
}

interface DealContextData {
  id: string;
  stage: string;
  status: string;
  property: {
    address: string;
    city: string;
    state: string;
    zip: string;
    mls_id?: string;
  };
  parties: {
    buyer: string;
    seller: string;
    listing_agent: string;
    selling_agent: string;
  };
  timeline: {
    created_on: string;
    closing_date: string;
  };
  financial: {
    price: number;
    down_payment: number;
    financing_type: string;
  };
}

const DealChatbotIntegration: React.FC<DealChatbotIntegrationProps> = ({
  dealId,
  userId,
  dealStage
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [cachePreloaded, setCachePreloaded] = useState(false);
  const [dealContext, setDealContext] = useState<DealContextData | null>(null);
  const [contextLoaded, setContextLoaded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch comprehensive deal context for enhanced chat performance
  const fetchDealContext = async () => {
    if (!dealId || contextLoaded) return;
    
    try {
      console.log('🔄 [DEAL CHAT] Fetching comprehensive deal context for enhanced AI performance');
      const response = await axiosInstance.get(`/deals/${dealId}`);
      const deal = response.data?.data?.deal;
      
      if (deal) {
        const contextData: DealContextData = {
          id: deal.id,
          stage: deal.deal_stage?.deal_stage_name || dealStage || 'Unknown',
          status: deal.deal_lead_status || 'Unknown',
          property: {
            address: deal.property_address || 'Not provided',
            city: deal.property_city || 'Not provided',
            state: deal.property_state || 'Not provided',
            zip: deal.property_zip || 'Not provided',
            mls_id: deal.property_mls_id || undefined
          },
          parties: {
            buyer: deal.deal_buyer_name || 'Not provided',
            seller: deal.deal_seller_name || 'Not provided',
            listing_agent: deal.deal_listing_agent_name || 'Not provided',
            selling_agent: deal.deal_selling_agent_name || 'Not provided'
          },
          timeline: {
            created_on: deal.created_on || 'Not provided',
            closing_date: deal.deal_closing_date || 'Not provided'
          },
          financial: {
            price: deal.deal_price || 0,
            down_payment: deal.deal_down_payment || 0,
            financing_type: deal.deal_financing_type || 'Not provided'
          }
        };
        
        setDealContext(contextData);
        setContextLoaded(true);
        console.log('✅ [DEAL CHAT] Comprehensive context loaded:', {
          stage: contextData.stage,
          address: contextData.property.address,
          buyer: contextData.parties.buyer,
          seller: contextData.parties.seller
        });
      }
    } catch (error) {
      console.error('❌ [DEAL CHAT] Failed to fetch deal context:', error);
      // Set minimal context to prevent blocking
      setDealContext({
        id: dealId,
        stage: dealStage || 'Unknown',
        status: 'Unknown',
        property: { address: 'Not available', city: '', state: '', zip: '' },
        parties: { buyer: 'Not available', seller: 'Not available', listing_agent: 'Not available', selling_agent: 'Not available' },
        timeline: { created_on: '', closing_date: '' },
        financial: { price: 0, down_payment: 0, financing_type: 'Not available' }
      });
      setContextLoaded(true);
    }
  };

  // Load deal context and trigger cache preload when component mounts
  useEffect(() => {
    if (dealId && userId) {
      // Fetch comprehensive deal context first
      fetchDealContext();
      
      // Then preload cache if not already done
      if (!cachePreloaded) {
        triggerDealCachePreload(dealId, userId)
          .then(() => {
            setCachePreloaded(true);
            console.log('✅ Deal cache preloaded');
          })
          .catch(err => {
            console.warn('Cache preload failed:', err);
          });
      }
    }
  }, [dealId, userId, cachePreloaded, contextLoaded]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle sending message
  const handleSendMessage = async (message: string) => {
    // Ensure we have deal context before sending
    if (!contextLoaded || !dealContext) {
      console.warn('⚠️ [DEAL CHAT] Context not loaded yet, waiting...');
      // Wait for context to load or use minimal context
      if (!dealContext) {
        setDealContext({
          id: dealId,
          stage: dealStage || 'Unknown',
          status: 'Unknown',
          property: { address: 'Loading...', city: '', state: '', zip: '' },
          parties: { buyer: 'Loading...', seller: 'Loading...', listing_agent: 'Loading...', selling_agent: 'Loading...' },
          timeline: { created_on: '', closing_date: '' },
          financial: { price: 0, down_payment: 0, financing_type: 'Unknown' }
        });
      }
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const startTime = Date.now();
      
      console.log('🚀 [DEAL CHAT] Sending message with comprehensive context:', {
        dealStage: dealContext?.stage,
        address: dealContext?.property?.address,
        buyer: dealContext?.parties?.buyer
      });
      
      // Call deal chatbot API with comprehensive context for optimal Claude Terminal Daemon performance
      const response = await llmAxiosInstance.post('/chat/internal/deal', {
        message,
        user_id: userId,
        deal_id: dealId,
        context: {
          deal_info: dealContext,
          workflow_context: {
            stage: dealContext?.stage,
            is_closed: dealContext?.stage?.toLowerCase().includes('closed') || false,
            is_under_contract: dealContext?.stage?.toLowerCase().includes('under contract') || false,
            is_pre_closing: dealContext?.stage?.toLowerCase().includes('pre-closing') || false,
            has_property_info: !!(dealContext?.property?.address && dealContext?.property?.address !== 'Not provided'),
            has_parties_info: !!(dealContext?.parties?.buyer && dealContext?.parties?.buyer !== 'Not provided'),
            has_financial_info: !!(dealContext?.financial?.price && dealContext?.financial?.price > 0)
          }
        }
      });

      const latency = Date.now() - startTime;
      
      // Add AI response
      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        message: response.data.response || response.data.message_text,
        timestamp: new Date(),
        cached: response.data.from_cache || false,
        latency: response.data.latency_ms || latency
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      // Log performance metrics
      if (aiMessage.cached) {
        console.log(`⚡ Cached response served in ${aiMessage.latency}ms`);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        type: 'ai',
        message: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Open chatbot with initial message
  const openWithMessage = (message: string) => {
    setIsOpen(true);
    // Small delay to ensure modal is open
    setTimeout(() => {
      handleSendMessage(message);
    }, 100);
  };

  return (
    <>
      {/* Floating Chatbot Button */}
      <Button
        variant="primary"
        className="floating-chatbot-btn"
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}
      >
        <FontAwesomeIcon icon={faComments} size="lg" />
        {cachePreloaded && (
          <Badge 
            bg="warning" 
            style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              fontSize: '10px'
            }}
          >
            ⚡
          </Badge>
        )}
      </Button>

      {/* Quick Action Shortcuts (Optional) */}
      <div 
        className="quick-action-shortcuts"
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          display: isOpen ? 'none' : 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}
      >
        <Button
          variant="light"
          size="sm"
          onClick={() => openWithMessage("What's the current status of this deal?")}
          className="shadow-sm"
        >
          <FontAwesomeIcon icon={faBolt} className="text-warning me-1" />
          Deal Status
        </Button>
        <Button
          variant="light"
          size="sm"
          onClick={() => openWithMessage("What tasks need immediate attention?")}
          className="shadow-sm"
        >
          <FontAwesomeIcon icon={faBolt} className="text-warning me-1" />
          Urgent Tasks
        </Button>
      </div>

      {/* Chatbot Modal */}
      <Modal
        show={isOpen}
        onHide={() => setIsOpen(false)}
        size="lg"
        className="deal-chatbot-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Deal Assistant
            {cachePreloaded && (
              <Badge bg="warning" className="ms-2">
                ⚡ Fast Mode
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Chat Messages */}
          {messages.length > 0 && (
            <div className="chat-messages mb-3">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`message ${msg.type} mb-2`}
                  style={{
                    textAlign: msg.type === 'user' ? 'right' : 'left'
                  }}
                >
                  <div
                    className={`message-bubble p-2 rounded ${
                      msg.type === 'user' ? 'bg-primary text-white' : 'bg-light'
                    }`}
                    style={{
                      display: 'inline-block',
                      maxWidth: '80%'
                    }}
                  >
                    {msg.message}
                    {msg.cached && (
                      <div className="text-muted small mt-1">
                        ⚡ Instant response ({msg.latency}ms)
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
          
          {/* Enhanced Chatbot UI */}
          <DealChatbotEnhanced
            dealId={dealId}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DealChatbotIntegration;